import { create, StateCreator } from "zustand";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { immer } from "zustand/middleware/immer";
import { IVendorUser } from "@smartrr/shared/entities/VendorUser";
import { bulkUpdatesEndpointsMap } from "../../libs/utils/constants";

export interface BulkUpdateFileUpload {
  loading: boolean;
  uploaded: boolean;
  error: string | null;
  user: IVendorUser | null;

  actions: {
    uploadFile(uploadedFileId: string, bulkActionType: string): Promise<void>;
    setUser(user: IVendorUser | null): void;
  };
}

const tableSlice: StateCreator<BulkUpdateFileUpload, [["zustand/immer", never]]> = (set, get) => ({
  loading: false,
  error: null,
  uploaded: false,
  user: null,
  actions: {
    async uploadFile(uploadedFileId, bulkActionType) {
      set(draft => {
        draft.error = "";
      });

      const user = get().user;

      if (!user) {
        return;
      }

      const response = await typedFrontendVendorApi.postReq(bulkUpdatesEndpointsMap[bulkActionType], {
        reqBody: {
          uploadedBy: user.originalEmail,
          uploadedFileId,
        },
      });

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Upload failed.";
        });
        return;
      }

      set(draft => {
        draft.uploaded = true;
      });
    },

    setUser(user) {
      if (user) {
        set(draft => {
          draft.user = user;
        });
      }
    },
  },
});

const useBulkUpdateFileUpload = create<BulkUpdateFileUpload>()(immer(tableSlice));

const initialStoreState = useBulkUpdateFileUpload.getState();

export const BulkUpdateFileUploadStoreAccess = {
  useActions() {
    return useBulkUpdateFileUpload(state => state.actions);
  },

  useLoading() {
    return useBulkUpdateFileUpload(state => state.loading);
  },

  useError() {
    return useBulkUpdateFileUpload(state => state.error);
  },

  testing: {
    store: useBulkUpdateFileUpload,
    actions() {
      return useBulkUpdateFileUpload.getState().actions;
    },
    reset() {
      return useBulkUpdateFileUpload.setState(initialStoreState);
    },
  },
};
